/**
 * Submission detail view with role variants (type: "bounty" | "owner" |
 * "accepted" | "admin"; submissionType: "normal" | "ai"). Routed at
 * "/account/listings/:bountyId/submissions/:submissionId" (type "bounty",
 * where the bounty owner can accept or reject), and composed by
 * SubmissionBountyDisplay ("owner"), BountyAcceptedSubmissionPage ("accepted",
 * fetched via UserBountySubmissionAPI.getAcceptedSubmission) and admin pages.
 * Accepting re-verifies availability (SubmissionVerificationAPI) then collects
 * a card via Stripe Elements/PaymentMethodForm and runs the payment through
 * handlePaymentMethodCreated.ts; product link is revealed on success.
 * Delete/reject go through BountySubmissionAPI, UserBountySubmissionAPI or
 * AdminAPI depending on the variant; errors surface via ErrorContext.
 */
import InputField from "@/components/InputField";
import { emptySubmissionImageText } from "@/constants/submission";
import { FieldConfig } from "@/components/InputField";
import LoadingSpinner from "@/components/LoadingSpinner";
import { SubmissionWithBounty } from "@/types/submission";
import { useState, useEffect } from "react";
import ImageCarousel from "@/components/ImageCarousel";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SubheadedPage } from "@/components/SubheadedPage";
import { Trash } from "lucide-react";
import { UserBountySubmissionAPI } from "@/api/userBountySubmission";
import { useUserState } from "@/contexts/UserContext";
import BountySubmissionAPI from "@/api/bountySubmission";
import AlertDialog from "@/components/AlertDialog";
import ItemLink from "@/components/ItemLink";
import { formatPostedDatetime } from "@/utils/submission";
import { FIELD_WIDTH, BUTTON_SIZE } from "@/constants/ui";
import { PaymentMethodForm } from "@/components/PaymentMethodForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getStripePublicKey } from "@/utils/config";
import { useError } from "@/contexts/ErrorContext";
import {
    HandlePaymentMethodCreatedResult,
    handlePaymentMethodCreated as handlePaymentMethodCreatedUtil,
} from "@/pages/submission/handlePaymentMethodCreated";
import { SubmissionVerificationAPI } from "@/api/submissionVerification";
import { AdminAPI } from "@/api/admin";

/**
 * Stripe payment flow overview (frontend ↔ backend)
 * -------------------------------------------------
 * Backend (see `backend/app/lib/stripe.py`):
 * - Creates a PaymentIntent using Destination Charges with `transfer_data.destination`
 *   set to the finder’s Connect account and `application_fee_amount` for platform fees.
 * - Confirms the PaymentIntent server-side with the provided Payment Method.
 * - Persists a `StripePayment` row and returns `AcceptSubmissionWithPaymentResult` with
 *   `payment_details: StripePaymentDetails` and an app-level `payment_status` that maps
 *   from the underlying Stripe PaymentIntent status.
 * - For SCA/3DS, may return `stripe_payment_status = REQUIRES_ACTION` and a
 *   `client_secret` for the frontend to complete authentication.
 *
 * Frontend (this component):
 * - Wraps the payment form in `<Elements>` and collects a Payment Method via
 *   `PaymentMethodForm`.
 * - Calls `UserBountySubmissionAPI.acceptSubmission(...)` with the Payment Method id,
 *   which triggers the backend flow described above.
 * - If `payment_status === SUCCEEDED`, it polls until the accepted submission appears
 *   (webhook-driven) and then reveals the item link.
 * - If `payment_status === PENDING` and `stripe_payment_status === REQUIRES_ACTION`,
 *   it calls `stripe.confirmCardPayment(client_secret)` to complete SCA/3DS. After
 *   success or while processing, it polls for webhook-driven acceptance.
 * - If `payment_status === CANCELLED`, it shows an error.
 *
 * Important returned fields (from `StripePaymentDetails`):
 * - `stripePaymentId`: Persisted payment row id.
 * - `paymentIntentId`: Stripe PaymentIntent id.
 * - `stripePaymentStatus`: Underlying PaymentIntent status (e.g., SUCCEEDED, REQUIRES_ACTION).
 * - `amountCharged`, `platformFee`, `payoutAmount`: Dollar amounts for UX.
 * - `clientSecret` (optional): Required for SCA/3DS via `confirmCardPayment`.
 */

/**
 * @param type - "bounty", "owner", or "accepted"
 *
 * If type is "bounty", the productUrl shouldn't be displayed
 * If type is "owner" or "accepted", the productUrl should be displayed
 */

type SubmissionDisplayType = "bounty" | "owner" | "accepted" | "admin";
type SubmissionType = "normal" | "ai";

interface SubmissionDisplayProps {
    type: SubmissionDisplayType;
    submissionType: SubmissionType;
    submission?: SubmissionWithBounty; // Optional prop for admin views
    onDelete?: () => void;
}

type AlertType = "delete" | "accept" | "reject";

interface AlertConfig {
    title: string;
    message: string;
    action: () => Promise<void>;
}

const SubmissionDisplay: React.FC<SubmissionDisplayProps> = ({
    type,
    submissionType,
    submission: propSubmission,
    onDelete,
}) => {
    const userState = useUserState();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { showError } = useError();
    const [submission, setSubmission] = useState<SubmissionWithBounty | null>(
        type === "accepted" ? null : propSubmission || location.state.submission
    );
    const [loading, setLoading] = useState(type === "accepted");
    const [showImageCarousel, setShowImageCarousel] = useState(false);
    const [currentAlert, setCurrentAlert] = useState<AlertType | null>(null);
    const [showItemLink, setShowItemLink] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [verifyingAvailability, setVerifyingAvailability] = useState(false);
    const [stripePromise, setStripePromise] = useState<any>(null);

    useEffect(() => {
        // Initialize Stripe.js with our publishable key so we can render Elements
        // Initialize Stripe after component mounts
        const stripePublicKey = getStripePublicKey();
        if (stripePublicKey) {
            setStripePromise(loadStripe(stripePublicKey));
        }
    }, []);

    useEffect(() => {
        // Fetch accepted submission if type is "accepted"
        if (type !== "accepted") return;

        const fetchAcceptedSubmission = async () => {
            try {
                setLoading(true);
                // Extract bountyId from URL parameters
                const bountyId = params.bountyId;

                if (!bountyId) {
                    showError("Bounty ID not found in URL");
                    return;
                }

                const { submission: acceptedSubmission } =
                    await UserBountySubmissionAPI.getAcceptedSubmission(
                        userState!.id,
                        parseInt(bountyId)
                    );
                setSubmission(acceptedSubmission as SubmissionWithBounty);
            } catch (error) {
                console.error("Failed to fetch accepted submission:", error);
                showError("failed to load accepted submission");
            } finally {
                setLoading(false);
            }
        };

        fetchAcceptedSubmission();
    }, [type, userState?.id, params.bountyId]);

    const descriptionConfig: FieldConfig = {
        key: "description",
        label: "description",
        type: "textarea",
        readonly: true,
        readonlyValue: submission?.description || "",
        fieldState: null,
        setFieldState: null,
    };

    const priceConfig: FieldConfig = {
        key: "price",
        label: "price",
        type: "number",
        readonly: true,
        readonlyValue: submission?.price || 0,
        fieldState: null,
        setFieldState: null,
    };

    const alertConfigs: Record<AlertType, AlertConfig> = {
        delete: {
            title: "delete submission",
            message:
                "are you sure you want to delete this submission? doing so will delete all the submissions associated with it.",
            action: async () => {
                if (!submission) return;
                if (type === "admin") {
                    if (!userState!.isAdmin) {
                        showError("Not admin in admin mode, this is a bug");
                        return;
                    }
                    if (submissionType === "normal") {
                        await AdminAPI.deleteSubmission(submission.id!);
                        navigate("/admin/submissions");
                    } else {
                        await AdminAPI.deleteAiSubmission(submission.id!);
                        navigate("/admin/submission-verification?tab=ai-submissions");
                    }
                } else {
                    await BountySubmissionAPI.deleteSubmission(submission.bountyId, submission.id!);
                    navigate("/account/submissions");
                }
                setCurrentAlert(null);
                onDelete?.();
            },
        },
        accept: {
            title: "accept submission",
            message: "are you sure you want to accept this submission?",
            action: async () => {
                if (!submission) return;
                setCurrentAlert(null);
                setVerifyingAvailability(true);

                try {
                    const productUrl = await BountySubmissionAPI.getProductUrl(
                        submission.bountyId,
                        submission.id!
                    );

                    const result = await SubmissionVerificationAPI.verifySubmission(
                        {
                            productUrl,
                            title: submission.title,
                            description: submission.description,
                            price: String(submission.price ?? 0),
                            imageUrls: submission.s3ImageUrls || [],
                            available: true,
                            isProductPage: true,
                        },
                        "accept"
                    );

                    if (!result.passed) {
                        showError("submission is no longer available");
                        return;
                    }

                    setShowPaymentForm(true);
                } finally {
                    setVerifyingAvailability(false);
                }
            },
        },
        reject: {
            title: "reject submission",
            message: "are you sure you want to reject this submission?",
            action: async () => {
                if (!submission) return;
                await UserBountySubmissionAPI.rejectSubmission(
                    userState!.id,
                    submission.bountyId,
                    submission.id!
                );
                setCurrentAlert(null);
                navigate(`/account/listings/${submission.bountyId}`);
            },
        },
    };

    const handleAction = async (alertType: AlertType) => {
        setLoading(true);
        try {
            await alertConfigs[alertType].action();
        } catch (error) {
            showError(`failed to ${alertType} submission`);
        } finally {
            setLoading(false);
            setCurrentAlert(null);
        }
    };

    /**
     * Handle the Payment Method created by `PaymentMethodForm`.
     *
     * Flow:
     * 1) Call `acceptSubmission` → backend creates and confirms PaymentIntent.
     * 2) Interpret `paymentStatus` (app-level) and `paymentDetails.stripePaymentStatus`.
     *    - SUCCEEDED: poll until the accepted submission appears (webhook update), then show link.
     *    - PENDING:
     *        a) If REQUIRES_PAYMENT_METHOD → show card-declined guidance.
     *        b) Else use `clientSecret` to run `confirmCardPayment` (SCA/3DS), then poll.
     *    - CANCELLED: show error.
     * 3) Surface unexpected statuses as errors.
     */
    const handlePaymentMethodCreated = async (
        paymentMethodId: string
    ): Promise<HandlePaymentMethodCreatedResult> => {
        if (!submission) return { success: false, errorMessage: "submission not found, bizarre" };
        return await handlePaymentMethodCreatedUtil({
            userId: userState!.id,
            submission,
            paymentMethodId,
            stripePromise,
        });
    };

    const renderButtons = () => {
        if (!submission || submission.status !== "validated") return null;

        const disableForPayment = type === "bounty" && showPaymentForm;
        const buttonsDisabled = loading || disableForPayment;

        if (userState!.id === submission.porsuiUserId || userState!.isAdmin) {
            return (
                <button
                    className={`p-2 rounded-full text-black font-porsui hover:bg-slate-100 transition-colors ${
                        !buttonsDisabled ? "text-black" : "text-slate-400 cursor-not-allowed"
                    }`}
                    onClick={() => setCurrentAlert("delete")}
                    disabled={buttonsDisabled}
                >
                    <Trash size={BUTTON_SIZE} />
                </button>
            );
        }
        return null;
    };

    // Show loading state if submission is not loaded yet
    if (!submission && type === "accepted") {
        return (
            <SubheadedPage heading="Loading...">
                <div className="flex justify-center items-center h-64">
                    <LoadingSpinner />
                </div>
            </SubheadedPage>
        );
    }

    const handleCheckAvailability = async () => {
        // Use the existing loading state
        if (loading || !submission) return;
        try {
            setLoading(true);
            const result = await SubmissionVerificationAPI.verifySubmission(
                {
                    productUrl: submission.productUrl!,
                    title: submission.title,
                    description: submission.description,
                    price: String(submission.price ?? 0),
                    imageUrls: submission.s3ImageUrls || [],
                    available: true,
                    isProductPage: true,
                },
                "accept"
            );
            if (!result.passed) {
                const failureMessage =
                    result.failureReasons.length > 0
                        ? result.failureReasons.join("\n")
                        : "Verification failed for unknown reason";
                showError(`Submission verification failed:\n${failureMessage}`);
            }
        } catch (error: any) {
            const message =
                error?.response?.data?.message || error?.message || "failed to check availability";
            showError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <SubheadedPage
            heading={submission?.title ?? ""}
            headerAction={
                <div className="w-full relative">
                    {submission && submission.status === "validated" && (
                        <div className="absolute top-[-24px] right-0 flex flex-row space-x-4">
                            {renderButtons()}
                        </div>
                    )}
                    {showImageCarousel && submission && (
                        <div className="md:fixed md:inset-0 flex md:items-center justify-center bg-black bg-opacity-50 z-50 overflow-auto">
                            <ImageCarousel
                                imageUrls={submission.s3ImageUrls ?? []}
                                onClose={() => setShowImageCarousel(false)}
                            />
                        </div>
                    )}
                </div>
            }
        >
            {loading || verifyingAvailability ? (
                <div className="flex flex-col justify-center items-center h-64 space-y-4">
                    <LoadingSpinner />
                    {verifyingAvailability && (
                        <p className="font-porsui text-slate-500">
                            verifying submission is still available...
                        </p>
                    )}
                </div>
            ) : (
                <div className={`flex flex-col ${showImageCarousel ? "opacity-50" : ""}`}>
                    <div className="w-full bg-slate-50 flex flex-col items-center md:flex-row md:items-start">
                        {/* Image Section */}
                        <div className="h-full flex flex-col md:mx-8 mb-4">
                            {/* Submission Image Section */}
                            <div className="relative w-72 h-72 cursor-pointer hover:shadow-xl transition-shadow duration-200 shadow-md rounded-xl">
                                {submission?.s3ImageUrls && submission.s3ImageUrls.length > 0 ? (
                                    <img
                                        src={submission.s3ImageUrls[0]}
                                        alt="bounty photo"
                                        className="w-full h-full object-cover rounded-xl"
                                        onClick={() => setShowImageCarousel(true)}
                                    />
                                ) : (
                                    <div className="w-full h-full rounded-xl overflow-hidden bg-gray-100">
                                        <div className="flex flex-col items-center justify-center h-full p-8">
                                            <span className="text-slate-500 font-porsui text-sm text-center whitespace-pre-line">
                                                {emptySubmissionImageText}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="text-sm text-slate-500 mt-2 ml-1">
                                {formatPostedDatetime(submission?.createdAt!)}
                            </div>
                        </div>
                        <div
                            className={`h-full ${FIELD_WIDTH} flex flex-col justify-between space-y-6`}
                        >
                            {/* Description Text Area */}
                            <InputField
                                key="description"
                                fieldConfig={descriptionConfig}
                                characterLimit={240}
                                className="flex-1"
                                height="h-full min-h-[132px]"
                            />
                            {/* Price */}
                            <InputField
                                key="price"
                                fieldConfig={priceConfig}
                                characterLimit={10}
                                className="flex-1"
                            />

                            {(type === "owner" || type === "accepted") && submission && (
                                <ItemLink itemLink={submission.productUrl ?? ""} />
                            )}
                            {type === "bounty" && submission && (
                                <div
                                    className={`transition-opacity duration-500 ease-in-out ${
                                        showItemLink ? "opacity-100" : "opacity-0"
                                    }`}
                                >
                                    <ItemLink itemLink={submission.productUrl ?? ""} />
                                </div>
                            )}
                        </div>
                        {type === "owner" && submission && submission.status === "accepted" && (
                            <div
                                className={`${FIELD_WIDTH} flex flex-col justify-between space-y-6 md:mx-8 mt-6`}
                            >
                                <button
                                    className="w-full shadow-md text-md h-10 py-2 px-2 justify-center items-center font-porsui text-black bg-white rounded-xl cursor-pointer hover:shadow-lg transition-shadow"
                                    onClick={handleCheckAvailability}
                                    disabled={loading}
                                >
                                    check availability
                                </button>
                            </div>
                        )}
                    </div>
                    {submission &&
                        submission.status === "validated" &&
                        userState!.id === submission.bounty.porsuiUserId && (
                            <div className="flex flex-row items-center justify-center md:justify-start space-x-24 p-8 md:ml-52">
                                <button
                                    className={`font-porsui text-lg transition-colors ${
                                        loading || (type === "bounty" && showPaymentForm)
                                            ? "text-slate-400 cursor-not-allowed"
                                            : "text-black hover:text-slate-600"
                                    }`}
                                    onClick={() => setCurrentAlert("accept")}
                                    disabled={loading || (type === "bounty" && showPaymentForm)}
                                >
                                    accept
                                </button>
                                <button
                                    className={`font-porsui text-lg transition-colors ${
                                        loading || (type === "bounty" && showPaymentForm)
                                            ? "text-slate-400 cursor-not-allowed"
                                            : "text-black hover:text-slate-600"
                                    }`}
                                    onClick={() => setCurrentAlert("reject")}
                                    disabled={loading || (type === "bounty" && showPaymentForm)}
                                >
                                    reject
                                </button>
                            </div>
                        )}
                    {showPaymentForm && stripePromise && submission && (
                        <div className="flex items-start w-full bg-slate-50 p-8">
                            <Elements stripe={stripePromise}>
                                <PaymentMethodForm
                                    onPaymentMethodCreated={async (paymentMethodId) => {
                                        const result =
                                            await handlePaymentMethodCreated(paymentMethodId);
                                        if (result.success) {
                                            setShowPaymentForm(false);
                                            setSubmission(
                                                result.submission as SubmissionWithBounty
                                            );
                                            setShowItemLink(true);
                                        } else {
                                            showError(
                                                result.errorMessage ||
                                                    "payment failed but no error message, bizarre"
                                            );
                                        }
                                        return result;
                                    }}
                                    onCancel={() => setShowPaymentForm(false)}
                                    amount={submission.bounty.reward}
                                    className="w-full max-w-4xl"
                                />
                            </Elements>
                        </div>
                    )}
                </div>
            )}
            {currentAlert && (
                <AlertDialog
                    title={alertConfigs[currentAlert].title}
                    message={alertConfigs[currentAlert].message}
                    onConfirm={() => handleAction(currentAlert)}
                    onCancel={() => setCurrentAlert(null)}
                />
            )}
        </SubheadedPage>
    );
};

export default SubmissionDisplay;
